import React, {
    Context,
    ReactNode,
    useCallback,
    useEffect,
    useMemo,
    useState,
} from 'react';
import { useQuery } from '@apollo/client';
import { useMachine, useService } from '@xstate/react';
import axios, { AxiosResponse } from 'axios';
import { useHistory } from 'react-router-dom';

import { queryStrings } from '@trqk/resolvers';
import {
    getEmptySelectionSet,
    getEmptySelectorState,
    getWebpackEnvVar,
    getStatementSelectionId,
    locale,
} from '@trqk/utils';

import { useTokenModal, useEntities, useToast } from '../../../../hooks';
import { URL_PARAMS } from '../../../../constants';

import {
    shortLabels,
    statementSelectionManagerMachine,
} from '../../../../hooks/useStatementSelection';
import { selectionIdPrefix } from '../constants';

const { GET_SESSION_CURRENT_USER } = queryStrings;

const emptySelectionSet = getEmptySelectionSet();
const emptySelectorState = getEmptySelectorState();

interface WorkDetailStatementSelectionProviderProps {
    /**
     * A list of statementIds which will be rendered as selectable statements. If an empty array
     * is provided, no statementIds will be rendered in the StatementSelector.
     */
    trqkSongStatementIds?: string[];
    children: ReactNode;
    /**
     * Callback invoked with new list of selected statement ids when the selection changes. If not
     * provided, the WorkDetailStatementSelectionProvider will set a URL param named `statement_ids` to a
     * concatenated list of the selected statement ids.
     */
    onSelectStatements: OnSelectStatements;
    /**
     * Optional prefix to apply to the selection ID used both as a localStorage key and as the state
     * machine ID within the statementSelectionManagerMachine
     */
    selectionIdPrefix?: string;
    StatementSelectionContext: Context<StatementSelectionProviderValue>;
}

export const WorkDetailStatementSelectionProvider = ({
    trqkSongStatementIds,
    children,
    onSelectStatements,
    StatementSelectionContext,
}: WorkDetailStatementSelectionProviderProps): JSX.Element => {
    const trqkDirectoryUrl = getWebpackEnvVar('web', 'TRQK_DIRECTORY_URL');
    const history = useHistory();

    const machine = useMachine<
        StatementSelectionManagerContext,
        StatementSelectionMetaEvent
    >(statementSelectionManagerMachine);
    const send = machine[1];
    const service = machine[2];
    const [state] = useService(service);
    const [isExpanded, setIsExpanded] = useState(false);
    const currentUserQuery = useQuery(GET_SESSION_CURRENT_USER);
    const userId = currentUserQuery?.data?.session?.currentUser?.id;
    const { selectedEntity } = useEntities();
    const { getToken } = useTokenModal();
    const { add: addToast } = useToast();
    const selectedEntityId = selectedEntity?.id ?? null;

    const selectionMachines =
        state?.context?.selectionMachines ?? ({} as StatementSelectionMachines);

    const selectionContexts = Object.values(selectionMachines).reduce(
        (
            acc: Record<string, StatementSelectionContext>,
            selectionMachine: StatementSelectionMachine,
        ): Record<string, StatementSelectionContext> => {
            acc[selectionMachine.state.context.id] =
                selectionMachine.state.context;

            return acc;
        },
        {} as Record<string, StatementSelectionContext>,
    );

    const selectionId = getStatementSelectionId({
        userId,
        entityId: selectedEntityId ?? '',
        prefix: selectionIdPrefix,
    });
    const selectedEntityPro = selectedEntity?.pro ?? null;
    const trqkSongStatementIdsString = JSON.stringify(trqkSongStatementIds);

    const addStatement = useCallback(
        ({
            statementId,
            entityId,
            handleNewStatement,
        }: AddStatementInput): void => {
            const trqkSongStatementIdsArray: string[] = JSON.parse(
                trqkSongStatementIdsString,
            );

            getToken((token: string): void => {
                const errorMessage =
                    locale.enUS.INGESTION.TOASTS.FETCH_STATEMENT_FAILURE;

                axios({
                    method: 'post',
                    url: trqkDirectoryUrl,
                    headers: {
                        Authorization: token,
                    },
                    data: {
                        query: `
                            query getTrqkStatementView($id: ID!) {
                                getTrqkStatementView(id: $id) {
                                    id
                                    earnings
                                    end_date
                                    name
                                    pro
                                    region
                                    role_type
                                    start_date
                                    statement_id
                                    status_name
                                    unit
                                    user_id
                                }
                            }
                        `,
                        variables: {
                            id: statementId,
                        },
                    },
                })
                    .then((response: AxiosResponse): void => {
                        const statementView: TrqkStatementView | null =
                            response?.data?.data?.getTrqkStatementView ?? null;

                        if (statementView !== null) {
                            if (handleNewStatement) {
                                handleNewStatement(statementView, entityId);
                            }

                            if (
                                !trqkSongStatementIdsArray ||
                                (statementView.statement_id &&
                                    trqkSongStatementIdsArray.includes(
                                        statementView.statement_id,
                                    ))
                            )
                                send({
                                    type: 'ADD_STATEMENT',
                                    value: {
                                        id: getStatementSelectionId({
                                            userId,
                                            entityId,
                                            prefix: selectionIdPrefix,
                                        }),
                                        entityId,
                                        shortLabels,
                                        statementView,
                                        userId,
                                    },
                                });
                        } else {
                            addToast({
                                kind: 'warning-banner',
                                content: errorMessage,
                            });
                        }
                    })
                    .catch(() => {
                        // If we encounter a missing statement here, it can be because the statement
                        // was deleted since the last time that log events were fetched, so we
                        // ignore the error. There could be other reasons for errors, but this is
                        // unlikely, and if there are missing statements, we will (hopefully) notice
                        // that in the UI.
                    });
            });
        },
        [trqkSongStatementIdsString, send, userId, addToast, getToken],
    );

    const selectionContext = selectionContexts[selectionId];
    const ready = selectionContext?.ready ?? true;
    const editing = selectionContext?.editing ?? null;
    const saved = selectionContext?.saved ?? emptySelectorState;
    const { regionFilter } = saved;
    const currentSelectionSet =
        saved.selectionSets[regionFilter] ?? emptySelectionSet;
    const statementsExist = useMemo(
        (): boolean =>
            Object.values(selectionContexts).some(
                ({ statementViewsMap }: StatementSelectionContext): boolean =>
                    statementViewsMap.size !== 0,
            ),
        [selectionContexts],
    );

    // When the selectedEntityId changes, use it to fetch the statementViews specified by the
    // trqkSongStatementIds prop and get access to a statementSelectionMachine. If the machine
    // doesn't exist yet, it will be created.
    useEffect(() => {
        const trqkSongStatementIdsArray: string[] = JSON.parse(
            trqkSongStatementIdsString,
        );

        if (
            selectedEntityId &&
            userId &&
            trqkSongStatementIdsArray.length > 0
        ) {
            const getStatementViewPromises = trqkSongStatementIdsArray.map(
                (statementId: string): Promise<TrqkStatementView> =>
                    new Promise((resolve, reject) => {
                        getToken((token: string): void => {
                            axios({
                                method: 'post',
                                url: trqkDirectoryUrl,
                                headers: {
                                    Authorization: token,
                                },
                                data: {
                                    query: `
                                    query getTrqkStatementView($id: ID!) {
                                        getTrqkStatementView(id: $id) {
                                            id
                                            earnings
                                            end_date
                                            name
                                            pro
                                            region
                                            role_type
                                            start_date
                                            statement_id
                                            status_name
                                            unit
                                            user_id
                                        }
                                    }
                                `,
                                    variables: {
                                        id: statementId,
                                    },
                                },
                            })
                                .then((response: AxiosResponse): void => {
                                    const statementView: TrqkStatementView | null =
                                        response?.data?.data
                                            ?.getTrqkStatementView ?? null;

                                    if (statementView) {
                                        resolve(statementView);
                                    } else {
                                        throw new Error();
                                    }
                                })
                                .catch(() => {
                                    reject(
                                        new Error(
                                            `Could not fetch statementView for id: ${statementId}`,
                                        ),
                                    );
                                });
                        });
                    }),
            );

            Promise.allSettled(getStatementViewPromises).then(
                (results: PromiseSettledResult<TrqkStatementView>[]): void => {
                    const statementViews = results.reduce(
                        (
                            acc: TrqkStatementView[],
                            curr:
                                | PromiseSettledResult<TrqkStatementView>
                                | PromiseRejectedResult,
                        ): TrqkStatementView[] => {
                            if (curr.status === 'fulfilled') {
                                acc.push(curr.value);
                            }

                            return acc;
                        },
                        [],
                    );

                    if (
                        trqkSongStatementIdsArray.length > 0 &&
                        statementViews.length < 1
                    ) {
                        addToast({
                            kind: 'warning-banner',
                            content:
                                locale.enUS.INGESTION.TOASTS
                                    .FETCH_STATEMENT_FAILURE,
                        });
                    }

                    // If there are any selected statement IDs specified in the URL, pass those
                    // in as the initial selectedIds
                    let urlParamSelectedIds = null;

                    const urlSearchParams = new URLSearchParams(
                        history.location.search,
                    );
                    const selectedIdsParam = urlSearchParams.get(
                        URL_PARAMS.DETAIL_STATEMENT_IDS,
                    );
                    const primary = urlSearchParams.get(
                        URL_PARAMS.DETAIL_STATEMENT_PRESET_PRIMARY,
                    ) as SelectionPresetWithRegion | null;
                    const fallback = urlSearchParams.get(
                        URL_PARAMS.DETAIL_STATEMENT_PRESET_FALLBACK,
                    ) as SelectionPresetWithRegion | null;

                    urlParamSelectedIds =
                        selectedIdsParam === null
                            ? null
                            : selectedIdsParam.split('-');

                    if (statementViews.length > 0) {
                        send({
                            type: 'USE_SELECTION_MACHINE',
                            value: {
                                id: selectionId,
                                entityId: selectedEntityId,
                                onSelectStatements,
                                presetsWithRegion: {
                                    primary,
                                    fallback,
                                },
                                pro: selectedEntityPro,
                                shortLabels,
                                statementViews,
                                userId,
                                urlParamSelectedIds,
                            },
                        });
                    }
                },
            );
        }
    }, [
        addToast,
        trqkSongStatementIdsString,
        getToken,
        history,
        onSelectStatements,
        selectedEntityId,
        selectedEntityPro,
        selectionId,
        send,
        userId,
    ]);

    const hasStatements = ready && statementsExist;
    const hasSelectedStatement =
        ready && currentSelectionSet.selectedIds.length > 0;

    useEffect(() => {
        if (
            hasStatements &&
            selectedEntityId !== null &&
            currentSelectionSet.selectedIds.length < 1
        ) {
            // selectableViews[0] will be the most recent selectable statement
            const newStatementId =
                currentSelectionSet.selectableViews?.[0]?.statement_id ?? null;

            if (newStatementId !== null) {
                send({
                    type: 'SET_SELECTED',
                    value: {
                        id: selectionId,
                        selectedStatementIds: [newStatementId],
                    },
                });
            }
        }
    }, [
        currentSelectionSet,
        hasStatements,
        regionFilter,
        selectedEntityId,
        send,
    ]);

    // This is provided so that a useEffect() call can compare subsequent values and only call
    // the getMergedStatementViews query when the selection changes.
    const selectedIdsString = JSON.stringify(currentSelectionSet.selectedIds);

    return (
        <StatementSelectionContext.Provider
            value={{
                statementSelectionProps: {
                    addStatement,
                    currentSelectionSet,
                    editing,
                    isExpanded,
                    hasStatements,
                    hasSelectedStatement,
                    pro: selectionContext?.pro ?? null,
                    ready,
                    saved,
                    selectedIdsString,
                    selectionId,
                    sendStatementSelectionEvent: send,
                    setIsExpanded,
                },
            }}
        >
            {children}
        </StatementSelectionContext.Provider>
    );
};
